// v2.1.0 - 2文字問題対応
import React, { useState, useMemo, useEffect } from 'react';
import { QUESTIONS } from '../constants/questions';
import { FL_SCRIPT_LABELS, FL_ROOM_STATUS } from '../shared/fiveLeague';
import { getAllRooms, cleanupOldRooms, cleanupInactiveRooms, deleteRoom } from '../shared/roomTx';
import { auth, loginWithEmail, isAdmin, ADMIN_EMAIL, onAuthChange } from '../firebase';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import {
  getQuestionsCount,
  getPendingQuestions,
  approveQuestion,
  rejectQuestion,
  addCategory,
  isDefaultCategory,
} from '../shared/questionsTx';
import './Admin.css';

// ローカルストレージのキー
const CUSTOM_QUESTIONS_KEY = 'five_league_custom_questions';
const DISABLED_QUESTIONS_KEY = 'five_league_disabled_questions';
const ADMIN_VERSION = '2.1.0'; // 2文字問題対応版
const EDITED_QUESTIONS_KEY = 'five_league_edited_questions';

// カスタム問題を取得
function getCustomQuestions() {
  try {
    const data = localStorage.getItem(CUSTOM_QUESTIONS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

// カスタム問題を保存
function saveCustomQuestions(questions) {
  localStorage.setItem(CUSTOM_QUESTIONS_KEY, JSON.stringify(questions));
}

// 削除された問題IDリストを取得
export function getDisabledQuestions() {
  try {
    const data = localStorage.getItem(DISABLED_QUESTIONS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

// 削除された問題IDリストを保存
function saveDisabledQuestions(questionIds) {
  localStorage.setItem(DISABLED_QUESTIONS_KEY, JSON.stringify(questionIds));
}

// 編集された組み込み問題を取得
function getEditedQuestions() {
  try {
    const data = localStorage.getItem(EDITED_QUESTIONS_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

// 編集された組み込み問題を保存
function saveEditedQuestions(edits) {
  localStorage.setItem(EDITED_QUESTIONS_KEY, JSON.stringify(edits));
}

function Admin({ navigate }) {
  // 認証状態
  const [authState, setAuthState] = useState('loading'); // loading, login, authorized
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // 認証チェック（セッション復元を待つ）
  useEffect(() => {
    // onAuthStateChangedは認証状態が確定してから呼ばれる
    // 初回呼び出し時にユーザー情報が復元されている
    const unsubscribe = onAuthChange((user) => {
      console.log('[Admin] Auth state changed:', user?.email);
      if (user?.email === ADMIN_EMAIL) {
        setAuthState('authorized');
      } else {
        setAuthState('login');
      }
    });

    return () => unsubscribe();
  }, []);

  // 管理者ログイン
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const userCredential = await loginWithEmail(loginEmail, loginPassword);
      // ログイン成功後、管理者かどうかチェック
      if (userCredential.user?.email !== ADMIN_EMAIL) {
        setLoginError('管理者権限がありません');
        // 管理者以外はログアウト
        await auth.signOut();
      }
      // onAuthChangeで状態が更新される
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setLoginError('メールアドレスまたはパスワードが正しくありません');
      } else {
        setLoginError('ログインに失敗しました');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // タブ管理
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' | 'rooms' | 'pending'

  // Firestore問題数
  const [firestoreCount, setFirestoreCount] = useState(null);

  // 承認待ち問題
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [editingPending, setEditingPending] = useState(null); // 編集中の承認待ち問題

  // 問題管理
  const [customQuestions, setCustomQuestions] = useState(() => getCustomQuestions());
  const [disabledQuestions, setDisabledQuestions] = useState(() => getDisabledQuestions());
  const [editedQuestions, setEditedQuestions] = useState(() => getEditedQuestions());
  const [filterLength, setFilterLength] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterScript, setFilterScript] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // ルーム管理
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [cleanupHours, setCleanupHours] = useState(24);
  const [inactiveMinutes, setInactiveMinutes] = useState(30);

  // ルーム一覧を取得
  const fetchRooms = async () => {
    setRoomsLoading(true);
    const result = await getAllRooms();
    if (result.ok) {
      // 作成日時でソート（新しい順）
      const sorted = result.rooms.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      setRooms(sorted);
    }
    setRoomsLoading(false);
  };

  // タブ切り替え時にルーム一覧を取得
  useEffect(() => {
    if (activeTab === 'rooms' && authState === 'authorized') {
      fetchRooms();
    }
  }, [activeTab, authState]);

  // Firestore問題数を取得
  useEffect(() => {
    if (authState === 'authorized') {
      checkFirestoreQuestions();
    }
  }, [authState]);

  // 管理者ログイン時に自動クリーンアップ（1時間以上操作がないルームを削除）
  useEffect(() => {
    if (authState === 'authorized') {
      const autoCleanup = async () => {
        const result = await cleanupInactiveRooms(60); // 60分以上非アクティブ
        if (result.ok && result.deletedCount > 0) {
          console.log(`[AutoCleanup] ${result.deletedCount}件の非アクティブルームを自動削除しました`);
        }
      };
      autoCleanup();
    }
  }, [authState]);

  // タブ切り替え時に承認待ち問題を取得
  useEffect(() => {
    if (activeTab === 'pending' && authState === 'authorized') {
      fetchPendingQuestions();
    }
  }, [activeTab, authState]);

  const checkFirestoreQuestions = async () => {
    const result = await getQuestionsCount();
    setFirestoreCount(result.count);
  };

  // 承認待ち問題を取得
  const fetchPendingQuestions = async () => {
    setPendingLoading(true);
    try {
      const result = await getPendingQuestions();
      if (result.ok) {
        setPendingQuestions(result.questions);
      }
    } catch (e) {
      console.error('Failed to fetch pending questions:', e);
    } finally {
      setPendingLoading(false);
    }
  };

  // 問題を承認
  const handleApprove = async (pendingId, editedData = null) => {
    const pending = pendingQuestions.find(q => q.id === pendingId);
    if (!pending) return;

    const finalData = editedData || pending;
    const category = finalData.category;

    // 新しいカテゴリの場合は確認
    if (!isDefaultCategory(category)) {
      if (!confirm(`新しいカテゴリ「${category}」を追加しますか？\n承認後、選択肢に追加されます。`)) {
        return;
      }
    } else if (!editedData) {
      if (!confirm('この問題を承認しますか？')) return;
    }

    const result = await approveQuestion(pendingId, editedData);
    if (result.ok) {
      // 新しいカテゴリを追加
      if (!isDefaultCategory(category)) {
        const catResult = await addCategory(category);
        if (catResult.ok) {
          console.log(`[Admin] Added category: ${category}`);
        }
      }
      alert('問題を承認しました');
      setEditingPending(null);
      fetchPendingQuestions();
      checkFirestoreQuestions();
    } else {
      alert('承認に失敗しました: ' + result.reason);
    }
  };

  // 問題を編集してから承認
  const handleEditApprove = (pending) => {
    setEditingPending(pending);
  };

  // 編集して承認を保存
  const handleSaveAndApprove = async (editedData) => {
    await handleApprove(editingPending.id, editedData);
  };

  // 問題を却下
  const handleReject = async (pendingId) => {
    const reason = prompt('却下理由を入力してください（任意）');
    if (reason === null) return; // キャンセル

    const result = await rejectQuestion(pendingId, reason);
    if (result.ok) {
      alert('問題を却下しました');
      fetchPendingQuestions();
    } else {
      alert('却下に失敗しました: ' + result.reason);
    }
  };

  // 古いルームを削除
  const handleCleanupRooms = async () => {
    if (!confirm(`${cleanupHours}時間以上古いルームを削除しますか？`)) return;

    const result = await cleanupOldRooms(cleanupHours);
    if (result.ok) {
      alert(`${result.deletedCount}件のルームを削除しました`);
      fetchRooms();
    } else {
      alert('削除に失敗しました: ' + result.reason);
    }
  };

  // 非アクティブなルームを削除
  const handleCleanupInactiveRooms = async () => {
    if (!confirm(`${inactiveMinutes}分以上操作がないルームを削除しますか？`)) return;

    const result = await cleanupInactiveRooms(inactiveMinutes);
    if (result.ok) {
      if (result.deletedCount > 0) {
        const details = result.deletedRooms
          .map(r => `  ${r.roomCode}: ${r.inactiveMinutes}分放置, ${r.playerCount}人`)
          .join('\n');
        alert(`${result.deletedCount}件の非アクティブルームを削除しました\n\n${details}`);
      } else {
        alert('削除対象のルームはありませんでした');
      }
      fetchRooms();
    } else {
      alert('削除に失敗しました: ' + result.reason);
    }
  };

  // 個別ルームを削除
  const handleDeleteRoom = async (roomId) => {
    if (!confirm('このルームを削除しますか？')) return;

    const result = await deleteRoom(roomId);
    if (result.ok) {
      setRooms(rooms.filter(r => r.id !== roomId));
    } else {
      alert('削除に失敗しました: ' + result.reason);
    }
  };

  // ルームステータスの表示名
  const getStatusLabel = (status) => {
    switch (status) {
      case FL_ROOM_STATUS.WAITING: return '待機中';
      case FL_ROOM_STATUS.PLAYING: return 'プレイ中';
      case FL_ROOM_STATUS.FINISHED: return '終了';
      default: return status;
    }
  };

  // 時間経過を表示
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '不明';
    const ms = timestamp.toMillis?.() || timestamp.seconds * 1000 || 0;
    const diff = Date.now() - ms;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}日前`;
    if (hours > 0) return `${hours}時間${minutes}分前`;
    return `${minutes}分前`;
  };

  // 全問題（組み込み + カスタム）
  const allQuestions = useMemo(() => {
    // 組み込み問題に編集を反映
    const builtInQuestions = QUESTIONS.map(q => {
      if (editedQuestions[q.id]) {
        return { ...q, ...editedQuestions[q.id] };
      }
      return q;
    });
    return [...builtInQuestions, ...customQuestions];
  }, [customQuestions, editedQuestions]);

  // フィルタリング
  const filteredQuestions = useMemo(() => {
    return allQuestions.filter(q => {
      if (filterLength !== 'all' && q.length !== parseInt(filterLength)) return false;
      if (filterDifficulty !== 'all' && q.difficulty !== parseInt(filterDifficulty)) return false;
      if (filterScript !== 'all' && q.script !== filterScript) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        if (!q.prompt.toLowerCase().includes(search) &&
            !q.answer.join('').toLowerCase().includes(search) &&
            !q.category.toLowerCase().includes(search)) {
          return false;
        }
      }
      return true;
    });
  }, [allQuestions, filterLength, filterDifficulty, filterScript, searchText]);

  // 統計
  const stats = useMemo(() => {
    const byLength = {};
    const byDifficulty = {};
    const byScript = {};
    allQuestions.forEach(q => {
      byLength[q.length] = (byLength[q.length] || 0) + 1;
      byDifficulty[q.difficulty] = (byDifficulty[q.difficulty] || 0) + 1;
      byScript[q.script] = (byScript[q.script] || 0) + 1;
    });
    return { byLength, byDifficulty, byScript, total: allQuestions.length, custom: customQuestions.length };
  }, [allQuestions, customQuestions]);

  // 問題を保存
  const handleSaveQuestion = (question) => {
    const isCustom = question.id.startsWith('custom-');

    if (isCustom) {
      // 既存のカスタム問題を更新、または新規追加
      const updated = customQuestions.map(q => q.id === question.id ? question : q);
      if (!customQuestions.find(q => q.id === question.id)) {
        updated.push(question);
      }
      setCustomQuestions(updated);
      saveCustomQuestions(updated);
    } else {
      // 組み込み問題を編集した場合は、編集内容を保存
      const updated = {
        ...editedQuestions,
        [question.id]: {
          category: question.category,
          difficulty: question.difficulty,
          length: question.length,
          script: question.script,
          answer: question.answer,
          prompt: question.prompt,
        },
      };
      setEditedQuestions(updated);
      saveEditedQuestions(updated);
    }

    setEditingQuestion(null);
    setShowAddForm(false);
  };

  // 問題を削除
  // 問題を削除
  const handleDeleteQuestion = (questionId) => {
    if (!confirm('この問題を削除しますか？')) return;

    const isCustom = questionId.startsWith('custom-');

    if (isCustom) {
      // カスタム問題は完全に削除
      const updated = customQuestions.filter(q => q.id !== questionId);
      setCustomQuestions(updated);
      saveCustomQuestions(updated);
    } else {
      // 組み込み問題は無効化リストに追加
      const updated = [...disabledQuestions, questionId];
      setDisabledQuestions(updated);
      saveDisabledQuestions(updated);
      // 編集内容も削除
      if (editedQuestions[questionId]) {
        const newEdited = { ...editedQuestions };
        delete newEdited[questionId];
        setEditedQuestions(newEdited);
        saveEditedQuestions(newEdited);
      }
    }
  };

  // 削除した組み込み問題を復元
  const handleRestoreQuestion = (questionId) => {
    const updated = disabledQuestions.filter(id => id !== questionId);
    setDisabledQuestions(updated);
    saveDisabledQuestions(updated);
  };

  // 新規問題作成
  const handleAddNew = () => {
    const newQuestion = {
      id: `custom-${Date.now()}`,
      category: '',
      difficulty: 1,
      length: 3,
      script: 'hiragana',
      answer: ['', '', ''],
      prompt: '',
    };
    setEditingQuestion(newQuestion);
    setShowAddForm(true);
  };

  // エクスポート
  const handleExport = () => {
    const json = JSON.stringify(customQuestions, null, 2);
    navigator.clipboard.writeText(json);
    alert('カスタム問題をクリップボードにコピーしました');
  };

  // CSVテンプレートダウンロード
  const handleDownloadTemplate = () => {
    const header = 'category,difficulty,length,script,answer,prompt';
    const examples = [
      '動物,1,3,hiragana,いぬね,忠誠心が強く「人間の最良の友」と呼ばれるペット（ひらがな3文字）',
      '科学,2,3,alphabet,DNA,生物の遺伝情報を担う物質。二重らせん構造を持つ（アルファベット大文字3文字）',
      '数学,3,4,number,3141,円周率πの小数点以下最初の4桁。3.○○○○...（半角数字4文字）',
      '地理,2,4,katakana,パリス,フランスの首都。エッフェル塔がある「花の都」（カタカナ4文字）',
      '歴史,4,4,kanji,徳川家康,関ヶ原の戦いに勝利し江戸幕府を開いた武将（漢字4文字）',
    ];
    const csv = [header, ...examples].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // CSVインポート
  const handleImportCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('CSVにデータがありません');
          return;
        }

        // ヘッダー確認
        const header = lines[0].toLowerCase();
        if (!header.includes('category') || !header.includes('answer') || !header.includes('prompt')) {
          alert('CSVヘッダーが不正です。テンプレートを参照してください。');
          return;
        }

        const newQuestions = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // CSV行をパース（カンマ区切り、引用符対応）
          const cols = parseCSVLine(line);
          if (cols.length < 6) {
            errors.push(`行${i + 1}: 列数不足`);
            continue;
          }

          const [category, difficulty, length, script, answer, prompt] = cols;

          // バリデーション
          const diffNum = parseInt(difficulty);
          const lenNum = parseInt(length);

          if (!category.trim()) {
            errors.push(`行${i + 1}: カテゴリが空です`);
            continue;
          }
          if (isNaN(diffNum) || diffNum < 1 || diffNum > 5) {
            errors.push(`行${i + 1}: 難易度は1-5の数値で指定してください`);
            continue;
          }
          if (isNaN(lenNum) || lenNum < 2 || lenNum > 6) {
            errors.push(`行${i + 1}: 文字数は2-6で指定してください`);
            continue;
          }
          if (!['hiragana', 'katakana', 'kanji', 'number', 'alphabet'].includes(script)) {
            errors.push(`行${i + 1}: scriptはhiragana/katakana/kanji/number/alphabetのいずれか`);
            continue;
          }
          if (answer.length !== lenNum) {
            errors.push(`行${i + 1}: 答えの文字数(${answer.length})がlength(${lenNum})と一致しません`);
            continue;
          }
          if (!prompt.trim()) {
            errors.push(`行${i + 1}: 問題文が空です`);
            continue;
          }

          newQuestions.push({
            id: `custom-${Date.now()}-${i}`,
            category: category.trim(),
            difficulty: diffNum,
            length: lenNum,
            script: script,
            answer: answer.split(''),
            prompt: prompt.trim(),
          });
        }

        if (errors.length > 0) {
          alert(`エラーがあります:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...他${errors.length - 5}件` : ''}`);
          if (newQuestions.length === 0) return;
          if (!confirm(`${newQuestions.length}件は正常です。インポートしますか？`)) return;
        }

        if (newQuestions.length === 0) {
          alert('インポートできる問題がありません');
          return;
        }

        const updated = [...customQuestions, ...newQuestions];
        setCustomQuestions(updated);
        saveCustomQuestions(updated);
        alert(`${newQuestions.length}件の問題をインポートしました`);
      } catch (err) {
        console.error(err);
        alert('CSVの解析に失敗しました');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // 同じファイルを再選択可能に
  };

  // CSV行パース（引用符対応）
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // ローディング中
  if (authState === 'loading') {
    return (
      <div className="admin-container admin-auth-container">
        <CyberBackground showParticles={false} />
        <div className="admin-auth-content">
          <NeonText color="blue" size="lg">読み込み中...</NeonText>
        </div>
      </div>
    );
  }

  // 管理者ログイン画面
  if (authState === 'login') {
    return (
      <div className="admin-container admin-auth-container">
        <CyberBackground showParticles={false} />
        <div className="admin-auth-content">
          <button className="back-btn admin-back-btn" onClick={() => navigate('home')}>
            ← ホームに戻る
          </button>

          <GlowingBorder color="pink" className="admin-login-box">
            <h2 className="admin-login-title">
              <NeonText color="pink" size="lg">管理者<br />ログイン</NeonText>
            </h2>

            <form onSubmit={handleAdminLogin} className="admin-login-form">
              <div className="admin-login-field">
                <label>メールアドレス</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="admin@example.com"
                  disabled={loginLoading}
                  autoComplete="email"
                />
              </div>

              <div className="admin-login-field">
                <label>パスワード</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="パスワード"
                  disabled={loginLoading}
                  autoComplete="current-password"
                />
              </div>

              {loginError && <p className="admin-login-error">{loginError}</p>}

              <CyberButton
                type="submit"
                variant="primary"
                className="admin-login-btn"
                disabled={loginLoading}
              >
                {loginLoading ? 'ログイン中...' : 'ログイン'}
              </CyberButton>
            </form>
          </GlowingBorder>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <button className="back-btn" onClick={() => navigate('home')}>← 戻る</button>
        <h1>管理画面 <span style={{fontSize:'0.5em',opacity:0.5}}>v{ADMIN_VERSION}</span></h1>
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'questions' ? 'active' : ''}`}
            onClick={() => setActiveTab('questions')}
          >
            問題管理
          </button>
          <button
            className={`admin-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            承認待ち {pendingQuestions.length > 0 && <span className="pending-badge">{pendingQuestions.length}</span>}
          </button>
          <button
            className={`admin-tab ${activeTab === 'rooms' ? 'active' : ''}`}
            onClick={() => setActiveTab('rooms')}
          >
            ルーム管理
          </button>
        </div>
      </header>

      {activeTab === 'pending' ? (
        /* 承認待ち問題タブ */
        <div className="admin-pending">
          <div className="pending-header">
            <span className="pending-count">{pendingQuestions.length}件の承認待ち問題</span>
            <button className="refresh-btn" onClick={fetchPendingQuestions} disabled={pendingLoading}>
              🔄 更新
            </button>
          </div>

          <div className="pending-list">
            {pendingLoading ? (
              <div className="loading-pending">読み込み中...</div>
            ) : pendingQuestions.length === 0 ? (
              <div className="no-pending">承認待ちの問題はありません</div>
            ) : (
              pendingQuestions.map(q => (
                <div key={q.id} className="pending-row">
                  <div className="pending-info">
                    <div className="pending-tags">
                      <span className="tag length">{q.length}文字</span>
                      <span className="tag difficulty">Lv.{q.difficulty}</span>
                      <span className="tag script">{FL_SCRIPT_LABELS[q.script]}</span>
                      <span className={`tag category ${!isDefaultCategory(q.category) ? 'new-category' : ''}`}>
                        {q.category}
                        {!isDefaultCategory(q.category) && <span className="new-badge">NEW</span>}
                      </span>
                    </div>
                    <div className="pending-prompt">{q.prompt}</div>
                    <div className="pending-answer">
                      <span className="answer-label">答:</span>
                      <span className="answer-text">{q.answer?.join('') || '(不明)'}</span>
                    </div>
                    <div className="pending-meta">
                      投稿者: {q.createdByEmail || q.createdBy?.slice(0, 8) || '匿名'}
                    </div>
                  </div>
                  <div className="pending-actions">
                    <button className="action-btn edit-approve" onClick={() => handleEditApprove(q)}>修正して承認</button>
                    <button className="action-btn approve" onClick={() => handleApprove(q.id)}>承認</button>
                    <button className="action-btn reject" onClick={() => handleReject(q.id)}>却下</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 編集して承認モーダル */}
          {editingPending && (
            <PendingEditModal
              question={editingPending}
              onSave={handleSaveAndApprove}
              onClose={() => setEditingPending(null)}
            />
          )}
        </div>
      ) : activeTab === 'rooms' ? (
        /* ルーム管理タブ */
        <div className="admin-rooms">
          <div className="room-cleanup-bar">
            <span className="room-count">{rooms.length}件のルーム</span>
            <div className="cleanup-controls">
              <select
                value={inactiveMinutes}
                onChange={(e) => setInactiveMinutes(parseInt(e.target.value))}
                className="cleanup-select"
              >
                <option value={10}>10分以上放置</option>
                <option value={30}>30分以上放置</option>
                <option value={60}>1時間以上放置</option>
                <option value={120}>2時間以上放置</option>
              </select>
              <button className="cleanup-btn inactive-cleanup" onClick={handleCleanupInactiveRooms}>
                ⏱️ 非アクティブ削除
              </button>
              <select
                value={cleanupHours}
                onChange={(e) => setCleanupHours(parseInt(e.target.value))}
                className="cleanup-select"
              >
                <option value={1}>1時間以上古い</option>
                <option value={6}>6時間以上古い</option>
                <option value={24}>24時間以上古い</option>
                <option value={72}>3日以上古い</option>
                <option value={168}>1週間以上古い</option>
              </select>
              <button className="cleanup-btn" onClick={handleCleanupRooms}>
                🗑️ 一括削除
              </button>
              <button className="refresh-btn" onClick={fetchRooms} disabled={roomsLoading}>
                🔄 更新
              </button>
            </div>
          </div>

          <div className="rooms-list">
            {roomsLoading ? (
              <div className="loading-rooms">読み込み中...</div>
            ) : rooms.length === 0 ? (
              <div className="no-rooms">ルームがありません</div>
            ) : (
              rooms.map(room => (
                <div key={room.id} className={`room-row status-${room.status}`}>
                  <div className="room-info">
                    <div className="room-header">
                      <span className="room-code">{room.roomCode}</span>
                      <span className={`room-status ${room.status}`}>
                        {getStatusLabel(room.status)}
                      </span>
                    </div>
                    <div className="room-details">
                      <span className="room-players">
                        👥 {room.players?.length || 0}人
                      </span>
                      <span className="room-time">
                        作成: {getTimeAgo(room.createdAt)}
                      </span>
                      <span className="room-time">
                        更新: {getTimeAgo(room.updatedAt)}
                      </span>
                    </div>
                    {room.players && room.players.length > 0 && (
                      <div className="room-player-names">
                        {room.players.map(p => p.nickname).join(', ')}
                      </div>
                    )}
                  </div>
                  <button
                    className="delete-room-btn"
                    onClick={() => handleDeleteRoom(room.id)}
                  >
                    削除
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        /* 問題管理タブ */
        <>
          {/* Firestore問題数 */}
          <div className="admin-question-header">
            <div className="sync-status">
              <span className="sync-label">Firestore:</span>
              {firestoreCount === null ? (
                <span className="sync-loading">確認中...</span>
              ) : (
                <span className="sync-count">{firestoreCount}件</span>
              )}
            </div>
            <button className="add-btn" onClick={handleAddNew}>+ 追加</button>
          </div>

      {/* 検索 */}
      <div className="admin-search">
        <input
          type="text"
          className="search-input"
          placeholder="問題文・答え・カテゴリで検索..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText && (
          <button className="search-clear" onClick={() => setSearchText('')}>×</button>
        )}
      </div>

      {/* 文字数フィルター */}
      <div className="filter-section">
        <div className="filter-label">文字数</div>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterLength === 'all' ? 'active' : ''}`}
            onClick={() => setFilterLength('all')}
          >
            全て <span className="filter-count">{stats.total}</span>
          </button>
          {[2, 3, 4, 5, 6].map(len => (
            <button
              key={len}
              className={`filter-btn ${filterLength === String(len) ? 'active' : ''}`}
              onClick={() => setFilterLength(String(len))}
            >
              {len}文字 <span className="filter-count">{stats.byLength[len] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 難易度フィルター */}
      <div className="filter-section">
        <div className="filter-label">難易度</div>
        <div className="filter-buttons">
          <button
            className={`filter-btn small ${filterDifficulty === 'all' ? 'active' : ''}`}
            onClick={() => setFilterDifficulty('all')}
          >
            全て
          </button>
          {[1, 2, 3, 4, 5].map(d => (
            <button
              key={d}
              className={`filter-btn small ${filterDifficulty === String(d) ? 'active' : ''}`}
              onClick={() => setFilterDifficulty(String(d))}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* 文字種フィルター */}
      <div className="filter-section">
        <div className="filter-label">文字種</div>
        <div className="filter-buttons">
          <button
            className={`filter-btn small ${filterScript === 'all' ? 'active' : ''}`}
            onClick={() => setFilterScript('all')}
          >
            全て
          </button>
          <button
            className={`filter-btn small ${filterScript === 'hiragana' ? 'active' : ''}`}
            onClick={() => setFilterScript('hiragana')}
          >
            ひらがな
          </button>
          <button
            className={`filter-btn small ${filterScript === 'katakana' ? 'active' : ''}`}
            onClick={() => setFilterScript('katakana')}
          >
            カタカナ
          </button>
          <button
            className={`filter-btn small ${filterScript === 'kanji' ? 'active' : ''}`}
            onClick={() => setFilterScript('kanji')}
          >
            漢字
          </button>
          <button
            className={`filter-btn small ${filterScript === 'alphabet' ? 'active' : ''}`}
            onClick={() => setFilterScript('alphabet')}
          >
            英字
          </button>
          <button
            className={`filter-btn small ${filterScript === 'number' ? 'active' : ''}`}
            onClick={() => setFilterScript('number')}
          >
            数字
          </button>
        </div>
      </div>

      {/* アクションバー */}
      {/* アクションバー */}
      <div className="admin-action-bar">
        <span className="question-count">{filteredQuestions.length}件表示</span>
        <div className="action-buttons">
          <button className="template-btn" onClick={handleDownloadTemplate}>テンプレCSV</button>
          <label className="import-btn">
            CSVインポート
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              style={{ display: 'none' }}
            />
          </label>
          {customQuestions.length > 0 && (
            <button className="export-btn" onClick={handleExport}>JSONエクスポート</button>
          )}
        </div>
      </div>

      {/* 問題一覧 */}
      <div className="admin-questions-wrapper">
        <div className="admin-questions">
          {filteredQuestions.length === 0 ? (
            <div className="no-results">
              条件に一致する問題がありません
            </div>
          ) : (
            filteredQuestions.map((q, idx) => {
              const isDisabled = disabledQuestions.includes(q.id);
              const isCustom = q.id.startsWith('custom-');
              const isEdited = !isCustom && editedQuestions[q.id];
              return (
                <div key={q.id} className={`question-row ${isCustom ? 'custom' : ''} ${isDisabled ? 'disabled' : ''} ${isEdited ? 'edited' : ''}`}>
                  <div className="question-num">{idx + 1}</div>
                  <div className="question-info">
                    <div className="question-tags">
                      <span className="tag length">{q.length}文字</span>
                      <span className="tag difficulty">Lv.{q.difficulty}</span>
                      <span className="tag script">{FL_SCRIPT_LABELS[q.script]}</span>
                      <span className="tag category">{q.category}</span>
                      {isCustom && <span className="tag custom">カスタム</span>}
                      {isEdited && <span className="tag edited-tag">編集済</span>}
                      {isDisabled && <span className="tag disabled-tag">削除</span>}
                    </div>
                    <div className="question-prompt">{q.prompt}</div>
                    <div className="question-answer">
                      <span className="answer-label">答:</span>
                      <span className="answer-text">{q.answer.map(c => c.trim() || '＿').join('')}</span>
                    </div>
                  </div>
                  <div className="question-actions">
                    {!isDisabled && (
                      <button className="action-btn edit" onClick={() => setEditingQuestion(q)}>編集</button>
                    )}
                    {isDisabled ? (
                      <button className="action-btn enable" onClick={() => handleRestoreQuestion(q.id)}>復元</button>
                    ) : (
                      <button className="action-btn delete" onClick={() => handleDeleteQuestion(q.id)}>削除</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

          {/* 編集モーダル */}
          {editingQuestion && (
            <QuestionEditModal
              question={editingQuestion}
              onSave={handleSaveQuestion}
              onClose={() => { setEditingQuestion(null); setShowAddForm(false); }}
              isNew={showAddForm}
            />
          )}
        </>
      )}
    </div>
  );
}

// 承認待ち問題編集モーダル
function PendingEditModal({ question, onSave, onClose }) {
  const [formData, setFormData] = useState({ ...question });

  const handleAnswerChange = (idx, value) => {
    const newAnswer = [...formData.answer];
    newAnswer[idx] = value;
    setFormData({ ...formData, answer: newAnswer });
  };

  const handleLengthChange = (newLength) => {
    const len = parseInt(newLength);
    const newAnswer = [...formData.answer];
    while (newAnswer.length < len) newAnswer.push('');
    while (newAnswer.length > len) newAnswer.pop();
    setFormData({ ...formData, length: len, answer: newAnswer });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.prompt.trim()) {
      alert('問題文を入力してください');
      return;
    }
    if (!formData.category.trim()) {
      alert('カテゴリを入力してください');
      return;
    }
    if (formData.answer.some(a => !a.trim())) {
      alert('すべての答えを入力してください');
      return;
    }
    onSave({
      category: formData.category,
      difficulty: formData.difficulty,
      length: formData.length,
      script: formData.script,
      answer: formData.answer,
      prompt: formData.prompt,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>修正して承認</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <label>
              カテゴリ
              {!isDefaultCategory(formData.category) && (
                <span className="new-category-note">※ 新しいカテゴリとして追加されます</span>
              )}
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="例: 動物、食べ物、地理"
            />
          </div>
          <div className="form-row">
            <label>文字数</label>
            <select value={formData.length} onChange={(e) => handleLengthChange(e.target.value)}>
              <option value="2">2文字</option>
              <option value="3">3文字</option>
              <option value="4">4文字</option>
              <option value="5">5文字</option>
              <option value="6">6文字</option>
            </select>
          </div>
          <div className="form-row">
            <label>難易度</label>
            <select value={formData.difficulty} onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) })}>
              <option value="1">1 (かんたん)</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5 (超難問)</option>
            </select>
          </div>
          <div className="form-row">
            <label>文字種</label>
            <select value={formData.script} onChange={(e) => setFormData({ ...formData, script: e.target.value })}>
              <option value="hiragana">ひらがな</option>
              <option value="katakana">カタカナ</option>
              <option value="kanji">漢字</option>
              <option value="number">半角数字</option>
              <option value="alphabet">アルファベット大文字</option>
            </select>
          </div>
          <div className="form-row">
            <label>問題文</label>
            <textarea
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              placeholder="問題文を入力（答えが一意に特定できる説明）"
              rows={3}
            />
          </div>
          <div className="form-row">
            <label>答え（{formData.length}文字）</label>
            <div className="answer-inputs">
              {formData.answer.map((char, idx) => (
                <input
                  key={idx}
                  type="text"
                  className="answer-char-input"
                  value={char}
                  onChange={(e) => handleAnswerChange(idx, e.target.value.slice(0, 1))}
                  maxLength={1}
                  placeholder={`${idx + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>キャンセル</button>
            <button type="submit" className="save-btn approve-save">修正して承認</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 問題編集モーダル
function QuestionEditModal({ question, onSave, onClose, isNew }) {
  const [formData, setFormData] = useState({ ...question });

  const handleAnswerChange = (idx, value) => {
    const newAnswer = [...formData.answer];
    newAnswer[idx] = value;
    setFormData({ ...formData, answer: newAnswer });
  };

  const handleLengthChange = (newLength) => {
    const len = parseInt(newLength);
    const newAnswer = [...formData.answer];
    while (newAnswer.length < len) newAnswer.push('');
    while (newAnswer.length > len) newAnswer.pop();
    setFormData({ ...formData, length: len, answer: newAnswer });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.prompt.trim()) {
      alert('問題文を入力してください');
      return;
    }
    if (!formData.category.trim()) {
      alert('カテゴリを入力してください');
      return;
    }
    if (formData.answer.some(a => !a.trim())) {
      alert('すべての答えを入力してください');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{isNew ? '問題を追加' : '問題を編集'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <label>カテゴリ</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="例: 動物、食べ物、地理"
            />
          </div>
          <div className="form-row">
            <label>文字数</label>
            <select value={formData.length} onChange={(e) => handleLengthChange(e.target.value)}>
              <option value="2">2文字</option>
              <option value="3">3文字</option>
              <option value="4">4文字</option>
              <option value="5">5文字</option>
              <option value="6">6文字</option>
            </select>
          </div>
          <div className="form-row">
            <label>難易度</label>
            <select value={formData.difficulty} onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) })}>
              <option value="1">1 (かんたん)</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5 (超難問)</option>
            </select>
          </div>
          <div className="form-row">
            <label>文字種</label>
            <select value={formData.script} onChange={(e) => setFormData({ ...formData, script: e.target.value })}>
              <option value="hiragana">ひらがな</option>
              <option value="katakana">カタカナ</option>
              <option value="kanji">漢字</option>
              <option value="number">半角数字</option>
              <option value="alphabet">アルファベット大文字</option>
            </select>
          </div>
          <div className="form-row">
            <label>問題文</label>
            <textarea
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              placeholder="問題文を入力（答えが一意に特定できる説明）"
              rows={3}
            />
          </div>
          <div className="form-row">
            <label>答え（{formData.length}文字）</label>
            <div className="answer-inputs">
              {formData.answer.map((char, idx) => (
                <input
                  key={idx}
                  type="text"
                  className="answer-char-input"
                  value={char}
                  onChange={(e) => handleAnswerChange(idx, e.target.value.slice(0, 1))}
                  maxLength={1}
                  placeholder={`${idx + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>キャンセル</button>
            <button type="submit" className="save-btn">保存</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Admin;
